import { FormControl, FormHelperText, Grid, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import type { ErrorProps } from "../../../../utils/errors/extract-zod-tree-error-props";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { ChannelSelect } from "../../../channels/components/channel-select";
import {
  isChatChannel,
  isEmailChannel,
  isTextChannel,
  type ChannelName,
} from "../../../channels/types";
import { prettyChannelName } from "../../../channels/utils";
import type { FollowUpSegmentPerson } from "../../hooks/use-follow-up-moment-details";
import type { FollowUpChannelWithData } from "../../reducer/schema";
import { FollowUpBodySection } from "../follow-up-body/follow-up-body-section";
import { FewerRecipientsDialog } from "./fewer-recipients-dialog";
import { SwitchChannelDialog } from "./switch-channel-dialog";

interface FollowUpChannelSelectProps {
  enabledChannels: NonNullable<ChannelName>[];
  selectedChannel: NonNullable<ChannelName>;
  setChannel: (channelData: FollowUpChannelWithData) => void;
  segmentPersons: FollowUpSegmentPerson[];
  error: ErrorProps;
  disabled: boolean;
}

export const FollowUpChannelSelect: FC<FollowUpChannelSelectProps> = ({
  enabledChannels,
  selectedChannel,
  setChannel,
  segmentPersons,
  error,
  disabled,
}) => {
  const { t } = useTranslation();

  const { handleOpen, handleClose, ...confirmModal } = useDialog<{
    channel: NonNullable<ChannelName>;
    fewerRecipients: boolean;
    isSameChannelType: boolean;
  }>();

  const personsByChannel = useMemo(() => {
    return segmentPersons.reduce(
      (acc, person) => {
        Object.entries(person.channels).forEach(([channel, details]) => {
          const chan = channel as NonNullable<ChannelName>;

          if (!acc[chan]) acc[chan] = [details];
          else acc[chan].push(details);
        });

        return acc;
      },
      {} as Record<NonNullable<ChannelName>, string[]>,
    );
  }, [segmentPersons]);

  const handleSelect = useCallback(
    (newChannel: NonNullable<ChannelName>) => {
      if (newChannel === selectedChannel) return;

      const newCount = personsByChannel[newChannel]?.length ?? 0;
      const currentCount = personsByChannel[selectedChannel]?.length ?? 0;
      const fewerRecipients = newCount < currentCount;

      const isSameChannelType =
        (isEmailChannel(selectedChannel) && isEmailChannel(newChannel)) ||
        (isChatChannel(selectedChannel) && isChatChannel(newChannel)) ||
        (isTextChannel(selectedChannel) && isTextChannel(newChannel));

      if (isSameChannelType) {
        setChannel(defaultChannelData(newChannel));
        return;
      }

      handleOpen({ channel: newChannel, fewerRecipients, isSameChannelType });
    },
    [selectedChannel, personsByChannel, setChannel, handleOpen],
  );

  const handleConfirm = useCallback(() => {
    if (!confirmModal.data?.channel) return;

    setChannel(defaultChannelData(confirmModal.data.channel));

    handleClose();
  }, [confirmModal.data, setChannel, handleClose]);

  const channelName = prettyChannelName(confirmModal.data?.channel ?? null);

  return (
    <>
      <FollowUpBodySection title="Channel" subtitle="How would you like to send the Follow-Up?">
        <FormControl error={error?.error} disabled={disabled}>
          <Stack direction="column" spacing={1}>
            <Grid container spacing={1}>
              <ChannelSelect
                selectedChannel={selectedChannel}
                channels={enabledChannels}
                onSelect={handleSelect}
                disabled={disabled}
              />
            </Grid>
            {isChatChannel(selectedChannel) && (
              <Typography variant="caption" color="textSecondary">
                {t(
                  "Slack/Teams Follow-Ups can only be sent as direct messages - they cannot be sent to channels.",
                )}
              </Typography>
            )}
          </Stack>
          {error.helperText && <FormHelperText>{error.helperText}</FormHelperText>}
        </FormControl>
      </FollowUpBodySection>
      {confirmModal.data?.fewerRecipients ? (
        <FewerRecipientsDialog
          open={confirmModal.open}
          channelName={channelName}
          onConfirm={handleConfirm}
          onCancel={handleClose}
          isSameChannelType={confirmModal.data?.isSameChannelType ?? false}
        />
      ) : !confirmModal.data?.isSameChannelType ? (
        <SwitchChannelDialog
          open={confirmModal.open}
          channelName={channelName}
          onConfirm={handleConfirm}
          onCancel={handleClose}
        />
      ) : null}
    </>
  );
};

function defaultChannelData(channel: NonNullable<ChannelName>): FollowUpChannelWithData {
  switch (channel) {
    case "slack":
      return {
        channel: "slack",
        channelData: { slack_alias_id: null },
      };
    case "teams":
      return {
        channel: "teams",
        channelData: undefined,
      };
    case "twilio":
      return {
        channel: "twilio",
        channelData: undefined,
      };
    case "work_email":
    case "personal_email":
      return {
        channel: channel,
        channelData: {
          email_sender: "",
          email_subject: "",
          email_design_id: null,
        },
      };
  }
}
